import { cached, tracked } from '@glimmer/tracking';

import { Aggregation, BlockVisualization, CompareType } from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import DsDataRootTopLevel from 'webapp/models-custom/ds-data/root/top';
import VisualizationProgress from 'webapp/models-custom/visualization/progress';
import type DChartComparePoint from 'webapp/d-chart/point/compare-point';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

const RecordLimit = 1;

export default class VisualizationGauge extends VisualizationProgress {
	override visualizationType = BlockVisualization.GAUGE;

	override recordLimit = RecordLimit;

	@tracked minPoint: DChartPrimaryPoint | null = null;
	@tracked primaryPoint: DChartPrimaryPoint | null = null;
	@tracked maxPoint: DChartPrimaryPoint | null = null;
	@tracked goalPoint: DChartComparePoint | null = null;

	override updateDataCache() {
		super.updateDataCache();
		this.minPoint = this.series[0]?.points[0] ?? null;
		this.primaryPoint = this.series[0]?.points[1] ?? null;
		this.maxPoint = this.series[0]?.points[2] ?? null;
		this.goalPoint = this.getComparePointByCompareIndex(0, this.primaryPoint);
		this.numberPoints = this.prepareNumberPoints();
	}

	override clearDataCache() {
		super.clearDataCache();
		this.primaryPoint = null;
		this.maxPoint = null;
		this.goalPoint = null;
	}

	protected override getAutoMaxPointValue(
		primaryPoint: DChartPrimaryPoint,
		startValue: number,
	): number {
		// `this.goalPoint` is not set yet here — this is called from super's
		// `createPointsForOutput` before `updateDataCache` assigns it.
		const goalPoint = this.getComparePointByCompareIndex(0, primaryPoint);
		const largerPoint =
			goalPoint && (goalPoint.value || 0) > (primaryPoint.value || 0)
				? goalPoint
				: primaryPoint;
		return super.getAutoMaxPointValue(largerPoint, startValue);
	}

	// ------- visualization specifics --------

	override indexForMaxValueCompare = 1;

	@cached
	get showNumber() {
		return this.blockSetup.firstShownDataSelect?.aggregation === Aggregation.SUM;
	}

	// ------- visualization - data --------

	private prepareNumberPoints() {
		// create new output and remove its compares
		let output: DsDataOutputTopLevel | null = null;

		const ds = this.blockSetup.firstShownDataSelect;
		if (ds && this.showNumber) {
			const data = ds.data;
			if (data) {
				const root = new DsDataRootTopLevel(ds, data);
				root.compareRoots = []; // remove compares
				output = root.outputs[0] || null;
				if (output) {
					const point = this.createDChartPointsFromOutput(output)[0];
					if (point) {
						return [point];
					}
				}
			}
		}

		return [];
	}

	// ------- general helpers --------

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// show number on by default
		dataSelect.aggregation = Aggregation.SUM;

		// create goal compare on the DS
		this.addGoalCompareToDs(dataSelect);
	}

	private addGoalCompareToDs(dataSelect: DataSelect) {
		const limitations = dataSelect.limitations,
			compare = limitations.compare!;
		compare.updateFromFormula({ type: CompareType.GOAL });
		dataSelect.updateComparesFormulaArrayFromCompareModels(limitations.compareCMs);
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const ds = this.blockSetup.firstShownDataSelect;
		return hashCode(
			JSON.stringify({
				sn: this.showNumber,
				stl: this.block.showTrafficLights,
				sv: ds?.startValue,
			}),
		);
	}

	// ------- visualization switch helpers --------

	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);

		const ds = this.blockSetup.firstShownDataSelect;
		if (ds) {
			// set aggregation (showing number)
			ds.aggregation = Aggregation.SUM;
			// set the first compare to goal, remove others
			ds.removeCompares();
			this.loggerService.logToConsole('[Vis change] to Gauge: create goal compare');
			this.addGoalCompareToDs(ds);
		}
	}

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		const ds = this.blockSetup.firstShownDataSelect;
		if (ds) {
			// remove aggregation (showing number)
			ds.aggregation = null;
		}
	}

	static override recordLimit = RecordLimit;
}
